import { useTranslation } from "react-i18next";
import { PersonalData } from "../../../../redux/apiSpecifications/apiSubjects";
import styled from "styled-components";
import { FormRow } from "../../../../style/form";
import SegmentedSubjectType from "../../../Inputs/SegmentedSubjectType";
import { SubjectType } from "../../../../config/const";
import { InputTextStyled } from "../../../../style/Input";
import { RowSpacer } from "../../../../style/containers";
import useFormData from "../../../../hooks/useFormData";
import { useEffect, useMemo } from "react";
import { isNotEmpty } from "../../../../validation/common";
import FormModal from "../../../Modals/FormModal";

const SubjectPersonalDataFormWrapper = styled.div`
  display: flex;
  gap: 2em;

  .right {
    width: 100%;
  }
`;

export const validateSubjectPersonalDataForm = (
  personalData: PersonalData | undefined
) => {
  if (!personalData) return false;

  if (personalData.subjectType === "PF" && personalData.naturalSubject) {
    const { name, lastname, fiscalCode } = personalData.naturalSubject;

    return isNotEmpty(name) && isNotEmpty(lastname) && isNotEmpty(fiscalCode);
  } else if (
    personalData.subjectType === "PG" &&
    personalData.juridicalSubject
  ) {
    const { businessName, pIva } = personalData.juridicalSubject;

    return isNotEmpty(businessName) && isNotEmpty(pIva);
  }

  return false;
};

interface ISubjectPersonalDataFormProps {
  subjectId?: number;
  subjectPersonalData: PersonalData;
  readonly?: boolean;
  onChange?: (subjectPersonalData: PersonalData) => void;
}

const SubjectPersonalDataForm = ({
  subjectId,
  subjectPersonalData,
  readonly,
  onChange,
}: ISubjectPersonalDataFormProps) => {
  const { t } = useTranslation();

  const handleOnChange = (value: any, field: string) => {
    if (onChange) {
      if (field === "subjectType") {
        onChange({
          ...subjectPersonalData,
          subjectType: value,
          naturalSubject:
            value === "PG" || !subjectPersonalData.naturalSubject
              ? undefined
              : { ...subjectPersonalData.naturalSubject },
          juridicalSubject:
            value === "PF" || !subjectPersonalData.juridicalSubject
              ? undefined
              : { ...subjectPersonalData.juridicalSubject },
        });
      } else {
        const fieldSplitted = field.split(".");

        if (fieldSplitted[0] === "naturalSubject") {
          onChange({
            ...subjectPersonalData,
            naturalSubject: {
              ...subjectPersonalData.naturalSubject,
              [fieldSplitted[1]]: value,
            },
          });
        } else if (fieldSplitted[0] === "juridicalSubject") {
          onChange({
            ...subjectPersonalData,
            juridicalSubject: {
              ...subjectPersonalData.juridicalSubject,
              [fieldSplitted[1]]: value,
            },
          });
        }
      }
    }
  };

  return (
    <>
      <SubjectPersonalDataFormWrapper>
        <div>
          <FormRow>
            <SegmentedSubjectType
              value={(subjectPersonalData.subjectType as SubjectType) || "PF"}
              onChange={(type) => handleOnChange(type, "subjectType")}
              readonly={readonly || subjectId !== undefined}
              required={true}
            />
          </FormRow>
        </div>

        <div className="right">
          {subjectPersonalData.subjectType === "PF" && (
            <>
              <FormRow>
                <InputTextStyled
                  label={t("LABELS.subject-name")}
                  placeholder={t("PLACEHOLDERS.subject-name")}
                  onChange={(value) =>
                    handleOnChange(value, "naturalSubject.name")
                  }
                  value={subjectPersonalData.naturalSubject?.name || ""}
                  disabled={readonly}
                  required={true}
                />

                <RowSpacer />

                <InputTextStyled
                  label={t("LABELS.subject-lastname")}
                  placeholder={t("PLACEHOLDERS.subject-lastname")}
                  onChange={(value) =>
                    handleOnChange(value, "naturalSubject.lastname")
                  }
                  value={subjectPersonalData.naturalSubject?.lastname || ""}
                  disabled={readonly}
                  required={true}
                />

                <RowSpacer />

                <InputTextStyled
                  label={t("LABELS.subject-fiscal-code")}
                  placeholder={t("PLACEHOLDERS.subject-fiscal-code")}
                  onChange={(value) =>
                    handleOnChange(value, "naturalSubject.fiscalCode")
                  }
                  value={subjectPersonalData.naturalSubject?.fiscalCode || ""}
                  disabled={readonly}
                  required={true}
                />
              </FormRow>
            </>
          )}

          {subjectPersonalData.subjectType === "PG" && (
            <FormRow>
              <InputTextStyled
                label={t("LABELS.subject-business-name")}
                placeholder={t("PLACEHOLDERS.subject-business-name")}
                onChange={(value) =>
                  handleOnChange(value, "juridicalSubject.businessName")
                }
                value={subjectPersonalData.juridicalSubject?.businessName}
                disabled={readonly}
                required={true}
              />

              <RowSpacer />

              <InputTextStyled
                label={t("LABELS.subject-pIva")}
                placeholder={t("PLACEHOLDERS.subject-pIva")}
                onChange={(value) =>
                  handleOnChange(value, "juridicalSubject.pIva")
                }
                value={subjectPersonalData.juridicalSubject?.pIva}
                disabled={readonly}
                required={true}
              />

              <RowSpacer />

              <div style={{ flex: 1 }}></div>
            </FormRow>
          )}
        </div>
      </SubjectPersonalDataFormWrapper>
      {/* <pre>{JSON.stringify(subjectId, null, 2)}</pre> */}
    </>
  );
};

export default SubjectPersonalDataForm;

interface ISubjectPersonalDataModalProps {
  open: boolean;
  subjectId?: number;
  subjectPersonalData?: PersonalData;
  onCancel: () => void;
  onConfirm: (subjectPersonalData: PersonalData) => void;
}

export const SubjectPersonalDataModal = ({
  open,
  subjectId,
  subjectPersonalData,
  onCancel,
  onConfirm,
}: ISubjectPersonalDataModalProps) => {
  const { t } = useTranslation();

  const {
    formData: _subjectPersonalData,
    setFormData: setSubjectPersonalData,
  } = useFormData<PersonalData>();

  useEffect(() => {
    if (open) {
      setSubjectPersonalData(subjectPersonalData);
    }
  }, [open]);

  const handleOnConfirmSubjectPersonalData = () => {
    onConfirm({ ..._subjectPersonalData! });
  };

  const isSubjectPersonalDataValid = useMemo(() => {
    return validateSubjectPersonalDataForm(_subjectPersonalData);
  }, [_subjectPersonalData]);

  return (
    <FormModal
      open={open}
      isConfirmButtonDisabled={!isSubjectPersonalDataValid}
      onCancel={onCancel}
      onConfirm={handleOnConfirmSubjectPersonalData}
    >
      {_subjectPersonalData && (
        <>
          <SubjectPersonalDataForm
            subjectId={subjectId}
            subjectPersonalData={_subjectPersonalData}
            onChange={(subjectPersonalData) =>
              setSubjectPersonalData(subjectPersonalData)
            }
          />

          {/* <pre>{JSON.stringify(_subjectPersonalData, null, 2)}</pre> */}
        </>
      )}
    </FormModal>
  );
};
